// src/utils/startList.ts
//
// Veřejná startovní listina závodu races/{raceId}/registrations/{uid}
// (bezpečnost a, 3. 10. 2026). Profily users a přihlášky registrations už
// nejsou veřejně čitelné – veřejná tabule (DisplayView, i bez přihlášení),
// rozhodčí stanovišť (JudgeView), Živé výsledky (AdminLiveResults)
// i publikace výsledků (publishResults) berou údaje o střelci odsud.
//
// Řádek nese jen veřejná data (podle rozhodnutí majitele): jméno, členské
// číslo, státní občanství, kategorii v ROCE ZÁVODU (ne rok narození)
// a nováčka. Kategorie se počítá stejně jako při publikaci (ageCategoryOf).
// Členství (zaplacené v roce závodu) listina nenese – majitel 4. 10. 2026:
// veřejně není potřeba vidět, kdo je člen. Pole `member`, které zapisovala
// dřívější verze, synchronizace u všech řádků listiny smaže (syncStartList).
//
// Kdo zapisuje (firestore.rules):
//  - správce: syncStartList – údaje z profilů a přihlášek (LiveRaceSetup při
//    otevření, uložení rund a tlačítkem „Obnovit startovní listinu", Živé
//    výsledky při otevření, publikace správcem, příprava dat v AdminOverview
//    – jen závody s živým bodováním, ostatní listinu nepotřebují);
//    trvalý zákaz nováčka (newbieDisabled) zapisuje jen správce,
//  - hlavní rozhodčí: jen userId, raceId a isNewbie (LiveRaceSetup, JudgeView),
//  - střelec: smí jen smazat svůj řádek po odhlášení (PaymentRace).
// Řádek bez synchronizovaných údajů (jen userId/raceId/isNewbie – hlavní
// rozhodčí přepnul nováčka u střelce, kterého správce ještě nesynchronizoval)
// se čte jako „bez údajů": jméno z rundy, kategorie „–".
//
// Jméno střelce ve veřejných datech (rundy, listina, live_results, výsledky,
// tabule) nikdy není e-mail ani uid: safeShooterName / publicShooterName
// („St. č. N", když jiné jméno není) – utils/shooterName.ts, odsud
// re-exportované.

import { useEffect, useState } from "react";
import {
  collection,
  deleteField,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  runTransaction,
  serverTimestamp,
  where,
  type DocumentData,
} from "firebase/firestore";
import { db } from "../firebase";
import { ageCategoryOf, publishAgeLimitsOf, type AgeCategoryId, type PublishAgeLimits } from "./ageCategory";
import { isResultCategoryId, loadCategoryDefaults } from "./categories";
import { createBatchWriter } from "./firestoreBatch";
import { toDateSafe } from "./pricing";
import { reportWarning } from "./reportError";
import { profilePublicName, safeShooterName } from "./shooterName";

export { publicShooterName, safeShooterName } from "./shooterName";

/** Cesta ke startovní listině závodu (doc id = uid střelce). */
export const startListPath = (raceId: string): string => `races/${raceId}/registrations`;

export interface StartListRow {
  userId: string;
  /** "Jméno Příjmení" z profilu; "" = neznámé (použije se jméno z rundy). */
  name: string;
  /** Členské číslo jako v profilu ("042"); "" = bez čísla. */
  sequenceNumber: string;
  /** Státní občanství z profilu; "" = neuvedeno. */
  nationality: string;
  /**
   * Věková / pohlavní kategorie v roce závodu (bez nováčka). undefined = řádek
   * ještě nesynchronizoval správce (údaje z profilu chybí).
   */
  categoryId: AgeCategoryId | undefined;
  /** Nováček v tomto závodě ("ano"/"ne"); undefined = rozhodčí ani přihláška neurčili. */
  isNewbie: "ano" | "ne" | undefined;
  /** Kategorie Nováček je střelci zakázaná (profil nebo rozhodčí). */
  newbieDisabled: boolean;
}

const isAgeCategoryId = (value: unknown): value is AgeCategoryId =>
  isResultCategoryId(value) && value !== "novacci";

const text = (value: unknown): string => (typeof value === "string" ? value.trim() : "");

/**
 * Řádek z dokumentu startovní listiny (tolerantní ke starším holým řádkům).
 * Jméno zůstává, jak je uložené (synchronizace tak pozná a přepíše e-mail
 * uložený dřív) – zobrazuje se vždy přes safeShooterName / publicShooterName.
 * Pole `member` dřívější verze se nečte (smaže ho synchronizace).
 */
export function parseStartListRow(id: string, data: DocumentData | undefined): StartListRow {
  const d = data ?? {};
  const isNewbie = d.isNewbie === "ano" || d.isNewbie === "ne" ? d.isNewbie : undefined;
  return {
    userId: text(d.userId) || id,
    name: text(d.name),
    sequenceNumber:
      typeof d.sequenceNumber === "number" ? String(d.sequenceNumber) : text(d.sequenceNumber),
    nationality: text(d.nationality),
    categoryId: isAgeCategoryId(d.categoryId) ? d.categoryId : undefined,
    isNewbie,
    newbieDisabled: d.newbieDisabled === true,
  };
}

/**
 * Řádek má údaje z profilu (synchronizoval ho správce): má kategorii –
 * hlavní rozhodčí smí zapsat jen userId, raceId a isNewbie (pravidla).
 */
export function hasProfileData(row: StartListRow | null | undefined): boolean {
  return !!row && row.categoryId !== undefined;
}

/** Nováček v závodě: rozhodnutí rozhodčího / přihlášky a zároveň není zakázaný. */
export function isEffectiveNewbie(row: StartListRow | null | undefined): boolean {
  return !!row && row.isNewbie === "ano" && !row.newbieDisabled;
}

/** Příznaky nováčka z dat rundy (RoundShooter) – poslední záloha. */
export interface RoundNewbieFlags {
  isNewbie?: boolean;
  newbieDisabled?: boolean;
}

/**
 * Nováček v závodě (categoryId "novacci") – jako tabule a rozhodčí:
 *  - řádek startovní listiny s rozhodnutím (isNewbie "ano"/"ne" – přepíná
 *    rozhodčí, synchronizace ho přebírá z přihlášky) platí sám
 *    (isEffectiveNewbie – zákaz nováčka na řádku ho ruší),
 *  - jinak přihláška závodu (registrations; undefined = střelec bez
 *    přihlášky, nebo přihlášky nejdou přečíst – čte je jen správce a hlavní
 *    rozhodčí svého závodu),
 *  - jinak příznak z dat rundy.
 * Zákaz nováčka (newbieDisabled) z řádku i z dat rundy platí vždy – nikde se
 * neodvolává (data rundy ho nesou z profilu, JudgeView po „Zakázat trvale").
 */
export function resolveNewbie(
  row: StartListRow | null | undefined,
  registration: boolean | undefined,
  round: RoundNewbieFlags
): boolean {
  if (round.newbieDisabled === true) return false;
  if (row && row.isNewbie !== undefined) return isEffectiveNewbie(row);
  const flagged = registration !== undefined ? registration : round.isNewbie === true;
  return flagged && !(row && row.newbieDisabled);
}

/** Popisky kategorií na živé tabuli a v Živých výsledcích (beze změny proti dřívějšku). */
export const BOARD_CATEGORY_LABEL: Record<AgeCategoryId, string> = {
  juniori: "Junior",
  zeny: "Žena",
  muzi: "Muž",
  seniori: "Senior",
  veterani: "Veterán",
};

export const BOARD_NEWBIE_LABEL = "Nováček";

/** Pořadí kategorií na tabuli (Junior první, Nováček poslední, bez údajů úplně na konci). */
export const BOARD_CATEGORY_ORDER: Record<string, number> = {
  Junior: 0,
  Žena: 1,
  Muž: 2,
  Senior: 3,
  Veterán: 4,
  Nováček: 5,
};

/** Kategorie střelce na tabuli: Nováček, věková kategorie, jinak "–" (bez údajů). */
export function boardCategoryLabel(row: StartListRow | null | undefined): string {
  if (isEffectiveNewbie(row)) return BOARD_NEWBIE_LABEL;
  return row && row.categoryId ? BOARD_CATEGORY_LABEL[row.categoryId] : "–";
}

/** Rok závodu z jeho data (text "YYYY-MM-DD", Timestamp, Date); neznámé → null. */
export function raceYearOf(date: unknown): number | null {
  const d = toDateSafe(date);
  return d ? d.getFullYear() : null;
}

// ── Výpočet řádku (správce) ─────────────────────────────────────────────────

/** Pole řádku, která zapisuje synchronizace (bez syncedAt). */
export interface StartListFields {
  userId: string;
  raceId: string;
  name: string;
  sequenceNumber: string;
  nationality: string;
  categoryId: AgeCategoryId;
  /**
   * Nováček v závodě; undefined = neurčeno (bez přihlášky i bez rozhodnutí
   * rozhodčího – publikace pak vezme příznak z rundy), pole se nezapíše.
   */
  isNewbie: "ano" | "ne" | undefined;
  newbieDisabled: boolean;
}

export interface StartListInput {
  raceId: string;
  uid: string;
  /** Data users/{uid}. */
  profile: DocumentData;
  /** isNewbie z přihlášky závodu (registrations) – jen když řádek nováčka ještě nemá. */
  registrationNewbie?: boolean;
  /** Dosavadní řádek – rozhodnutí rozhodčího o nováčkovi má přednost. */
  existing?: StartListRow | null;
  year: number;
  limits: PublishAgeLimits;
}

/**
 * Řádek startovní listiny z profilu: kategorie v roce závodu jako publikace
 * (ageCategoryOf – junior před ženou, neznámý rok narození = veterán; bez
 * členství), nováček podle dosavadního řádku (rozhodčí), jinak podle
 * přihlášky; zákaz nováčka z profilu nebo z řádku (zapsal ho správce). Jméno
 * jen bezpečné (safeShooterName): e-mail ani uid se do veřejné listiny
 * nedostanou, ani ze staršího uloženého řádku.
 */
export function computeStartListRow(input: StartListInput): StartListFields {
  const { profile, existing, uid } = input;
  const name =
    profilePublicName(profile, uid) || (existing ? safeShooterName(existing.name, uid) : "");
  const isNewbie: "ano" | "ne" | undefined =
    existing && existing.isNewbie
      ? existing.isNewbie
      : input.registrationNewbie === undefined
      ? undefined
      : input.registrationNewbie
      ? "ano"
      : "ne";
  return {
    userId: input.uid,
    raceId: input.raceId,
    name,
    sequenceNumber:
      typeof profile.sequenceNumber === "number"
        ? String(profile.sequenceNumber)
        : text(profile.sequenceNumber),
    nationality: text(profile.nationality),
    categoryId: ageCategoryOf(profile, input.year, input.limits),
    isNewbie,
    newbieDisabled: profile.newbieDisabled === true || (existing ? existing.newbieDisabled : false),
  };
}

/** Liší se vypočtený řádek od uloženého (zápis jen při změně – šetří zápisy i čtenáře). */
export function startListRowChanged(
  existing: StartListRow | null | undefined,
  next: StartListFields
): boolean {
  if (!existing) return true;
  return (
    existing.name !== next.name ||
    existing.sequenceNumber !== next.sequenceNumber ||
    existing.nationality !== next.nationality ||
    existing.categoryId !== next.categoryId ||
    existing.isNewbie !== next.isNewbie ||
    existing.newbieDisabled !== next.newbieDisabled
  );
}

export interface SyncStartListResult {
  /** Počet zapsaných řádků (nové, změněné a řádky, kterým se smazalo pole member). */
  written: number;
  /** uid bez profilu users/{uid} – řádek se nezapsal. */
  missingProfiles: string[];
}

/** Nejvýš řádků v jedné transakci syncStartList (nováček z přihlášky, smazání pole member). */
const TX_ROWS = 100;

/**
 * Smaže pole `member` dřívější verze z daných řádků listiny – jen smazání
 * pole, v transakcích po TX_ROWS řádcích: řádek se přečte znovu a když
 * mezitím zmizel (střelec se odhlásil) nebo pole už nemá, přeskočí se
 * (update by zmizelý řádek nezaložil, ale shodil by celou transakci).
 * Vrací počet upravených řádků.
 */
async function removeMemberFields(raceId: string, rowIds: ReadonlyArray<string>): Promise<number> {
  let removed = 0;
  for (let i = 0; i < rowIds.length; i += TX_ROWS) {
    const chunk = rowIds.slice(i, i + TX_ROWS);
    const count = await runTransaction(db, async (tx) => {
      const refs = chunk.map((uid) => doc(db, startListPath(raceId), uid));
      const snaps = await Promise.all(refs.map((ref) => tx.get(ref)));
      let n = 0;
      snaps.forEach((snap, j) => {
        const current = snap.exists() ? snap.data() : undefined;
        if (current && current.member !== undefined) {
          tx.update(refs[j], { member: deleteField() });
          n += 1;
        }
      });
      return n;
    });
    removed += count;
  }
  return removed;
}

/**
 * Synchronizace startovní listiny z profilů a přihlášek – JEN SPRÁVCE
 * (čte cizí users a registrations). Zapíše řádky všech zadaných střelců,
 * u kterých se něco změnilo (merge – pole navíc nesmaže). Rok závodu určuje
 * kategorii; bez data závodu aktuální rok (jako publikace).
 * Chyba čtení nebo zápisu se propaguje (volající rozhodne, zda je fatální).
 *
 * Pole `member` (členství – zapisovala ho dřívější verze, majitel 4. 10. 2026:
 * nezveřejňovat) se smaže u VŠECH řádků listiny, které ho mají – i u řádku,
 * který se jinak nemění, u střelce bez profilu nebo mimo zadané střelce,
 * i když zadaný není nikdo (prázdný seznam: přečte se jen listina, profily
 * a přihlášky ne). Zapisovanému řádku spolu se zápisem, ostatním samostatně
 * v transakci (řádek, který mezitím zmizel – střelec se odhlásil –, se
 * nezaloží znovu). Další synchronizace už nic nezapíše.
 *
 * Souběh s rozhodčím (zápis jde až po načtení všech profilů, tedy sekundy
 * po přečtení listiny):
 *  - zákaz nováčka (newbieDisabled) je trvalý – zapisuje se jen true; false
 *    podle starého čtení by přepsal zákaz zapsaný mezitím,
 *  - nováček (isNewbie) se zapisuje jen řádku, který ho při čtení neměl
 *    (nový řádek, nebo bez rozhodnutí), a to v transakci: řádek se přečte
 *    znovu a má-li nováčka už teď (rozhodčí ho mezitím přepnul), zapíší se
 *    jen ostatní pole. Ostatní řádky jdou dávkou bez isNewbie.
 */
export async function syncStartList(
  raceId: string,
  raceDate: unknown,
  userIds: ReadonlyArray<string>
): Promise<SyncStartListResult> {
  const uids = Array.from(new Set(userIds.filter((u) => typeof u === "string" && u)));
  if (!raceId) return { written: 0, missingProfiles: [] };
  if (uids.length === 0) {
    // Nikoho k zápisu (hosté bez účtu, prázdný závod) – jen pole member
    const snap = await getDocs(collection(db, startListPath(raceId)));
    const withMember = snap.docs.filter((d) => d.data().member !== undefined).map((d) => d.id);
    return { written: await removeMemberFields(raceId, withMember), missingProfiles: [] };
  }

  const year = raceYearOf(raceDate) ?? new Date().getFullYear();
  const [defaults, rowsSnap, regsSnap, profiles] = await Promise.all([
    loadCategoryDefaults(),
    getDocs(collection(db, startListPath(raceId))),
    getDocs(query(collection(db, "registrations"), where("raceId", "==", raceId))),
    // Nečitelný profil (výpadek, smazaný účet) nesmí shodit synchronizaci
    // ostatních – takový střelec se vynechá jako bez profilu.
    Promise.all(
      uids.map(async (uid) => {
        try {
          const snap = await getDoc(doc(db, "users", uid));
          return [uid, snap.exists() ? snap.data() : null] as const;
        } catch (err) {
          reportWarning("syncStartList.profile", err, { raceId, uid });
          return [uid, null] as const;
        }
      })
    ),
  ]);
  const limits = publishAgeLimitsOf(defaults);

  const existing = new Map<string, StartListRow>();
  // Řádky s polem member dřívější verze – smaže se u všech (viz popis funkce)
  const withMember = new Set<string>();
  rowsSnap.docs.forEach((d) => {
    const data = d.data();
    existing.set(d.id, parseStartListRow(d.id, data));
    if (data.member !== undefined) withMember.add(d.id);
  });
  // Víc přihlášek téhož střelce (staré duplicity) → nováček jen, když to říkají všechny.
  const regNewbie = new Map<string, boolean>();
  regsSnap.docs.forEach((d) => {
    const data = d.data();
    const uid = text(data.userId) || d.id;
    const isNewbie = data.isNewbie === "ano";
    const previous = regNewbie.get(uid);
    regNewbie.set(uid, previous === undefined ? isNewbie : previous && isNewbie);
  });

  const writer = createBatchWriter(db);
  // Řádky, kterým se zapisuje i nováček z přihlášky (při čtení ho neměly)
  const newbieRows: Array<{ uid: string; fields: DocumentData; isNewbie: "ano" | "ne" }> = [];
  let written = 0;
  const missingProfiles: string[] = [];
  for (const [uid, profile] of profiles) {
    if (!profile) {
      missingProfiles.push(uid);
      continue;
    }
    const row = existing.get(uid) ?? null;
    const next = computeStartListRow({
      raceId,
      uid,
      profile,
      registrationNewbie: regNewbie.get(uid),
      existing: row,
      year,
      limits,
    });
    if (!startListRowChanged(row, next)) continue;
    // Pole member dřívější verze se smaže spolu se zápisem řádku (ostatní níž)
    const dropMember = withMember.delete(uid);
    const { isNewbie, newbieDisabled, ...rest } = next;
    const fields: DocumentData = {
      ...rest,
      ...(newbieDisabled ? { newbieDisabled: true } : {}),
      ...(dropMember ? { member: deleteField() } : {}),
      syncedAt: serverTimestamp(),
    };
    written += 1;
    if (isNewbie !== undefined && (row === null || row.isNewbie === undefined)) {
      newbieRows.push({ uid, fields, isNewbie });
      continue;
    }
    // Nováčka už zapsaného v řádku nepřepisovat (rozhoduje rozhodčí) – merge
    // vynechané pole nechá beze změny
    await writer.set(doc(db, startListPath(raceId), uid), fields, { merge: true });
  }
  await writer.commit();
  // Nováček z přihlášky: řádek znovu přečíst v transakci a zapsat ho, jen když
  // ho řádek ani teď nemá (viz popis funkce)
  for (let i = 0; i < newbieRows.length; i += TX_ROWS) {
    const chunk = newbieRows.slice(i, i + TX_ROWS);
    await runTransaction(db, async (tx) => {
      const refs = chunk.map((r) => doc(db, startListPath(raceId), r.uid));
      const snaps = await Promise.all(refs.map((ref) => tx.get(ref)));
      chunk.forEach((r, j) => {
        const current = snaps[j].exists() ? snaps[j].data() : undefined;
        const decided = !!current && (current.isNewbie === "ano" || current.isNewbie === "ne");
        tx.set(refs[j], decided ? r.fields : { ...r.fields, isNewbie: r.isNewbie }, { merge: true });
      });
    });
  }
  // Pole member ostatních řádků (beze změny, bez profilu, mimo zadané
  // střelce): jen smazání pole, v transakci – řádek, který mezitím zmizel,
  // update nezaloží znovu
  const removedMember = await removeMemberFields(raceId, Array.from(withMember));
  written += removedMember;
  if (missingProfiles.length > 0) {
    reportWarning("syncStartList.missingProfiles", "Střelci bez profilu", {
      raceId,
      count: missingProfiles.length,
    });
  }
  return { written, missingProfiles };
}

// ── Čtení (kdokoli, i nepřihlášený) ─────────────────────────────────────────

export interface StartListState {
  /** uid → řádek. */
  rows: Map<string, StartListRow>;
  /** První snímek dorazil (i prázdný). */
  ready: boolean;
  /** Chyba posluchače; null = bez chyby. */
  error: unknown;
}

const EMPTY_ROWS = new Map<string, StartListRow>();

/** Živá startovní listina závodu (onSnapshot); bez raceId prázdná. */
export function useStartList(raceId: string | null | undefined): StartListState {
  const [state, setState] = useState<StartListState>({
    rows: EMPTY_ROWS,
    ready: false,
    error: null,
  });
  useEffect(() => {
    if (!raceId) {
      setState({ rows: EMPTY_ROWS, ready: true, error: null });
      return;
    }
    setState({ rows: EMPTY_ROWS, ready: false, error: null });
    return onSnapshot(
      collection(db, startListPath(raceId)),
      (snap) => {
        const rows = new Map<string, StartListRow>();
        snap.docs.forEach((d) => rows.set(d.id, parseStartListRow(d.id, d.data())));
        setState({ rows, ready: true, error: null });
      },
      (err) => {
        reportWarning("useStartList", err, { raceId });
        setState((s) => ({ ...s, ready: true, error: err }));
      }
    );
  }, [raceId]);
  return state;
}

/** Jednorázové načtení startovní listiny (publikace). */
export async function loadStartList(raceId: string): Promise<Map<string, StartListRow>> {
  const snap = await getDocs(collection(db, startListPath(raceId)));
  const rows = new Map<string, StartListRow>();
  snap.docs.forEach((d) => rows.set(d.id, parseStartListRow(d.id, d.data())));
  return rows;
}
